import { useSession } from './useSession';
import { BUFFET_MODULE, KDS_MODULE, ORDERS_MODULE, type WaiterSession } from './session.types';

/**
 * Pure check, shared by the hooks and by non-React code (e.g. tests).
 * Fail-closed: no session, no module, or no action → false.
 * Action codes are compared case-insensitively (`send-kot` === `SEND-KOT`).
 */
export function can(session: WaiterSession | null, moduleCode: string, action: string): boolean {
  if (!session) return false;
  if (!session.modules.includes(moduleCode)) return false;
  const actions = session.actions[moduleCode];
  if (!actions) return false;
  const wanted = action.toLowerCase();
  return actions.some((a) => a.toLowerCase() === wanted);
}

/**
 * `useCan('send-kot')` → true when the session grants that action on
 * `dineops-orders`. Gates from api-contract.md → Permissions:
 *   open the app → view · seat / add lines → create or update ·
 *   Send KOT → send-kot · open price + approve open item → set-price ·
 *   cancel after acceptance → update
 */
export function useCan(action: string): boolean {
  const { session } = useSession();
  return can(session, ORDERS_MODULE, action);
}

/** Same check against the buffet board `dineops-buffet`: view · mark · close · bill (slice 3, crs-db fixes/090). */
export function useCanBuffet(action: string): boolean {
  const { session } = useSession();
  return can(session, BUFFET_MODULE, action);
}

/** Same check against the kitchen module `dineops-kds`. */
export function useCanKitchen(action: string): boolean {
  const { session } = useSession();
  return can(session, KDS_MODULE, action);
}
